import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAdmin } from '@/hooks/useAdmin';
import { useSubscription } from '@/hooks/useSubscription';
import { usePlatformSettings } from '@/hooks/usePlatformSettings';
import { Loader2 } from 'lucide-react';

interface ProtectedRouteProps {
  children: React.ReactNode;
  checkSubscription?: boolean;
}

export function ProtectedRoute({ children, checkSubscription = false }: ProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading: adminLoading } = useAdmin();
  const { isSubscribed, isLoading: subLoading, isAdmin: subIsAdmin } = useSubscription();
  const { isMaintenanceActive, isLoading: settingsLoading } = usePlatformSettings();

  const isActuallyAdmin = isAdmin || subIsAdmin;
  const loading = authLoading || (!!user && (adminLoading || subLoading)) || settingsLoading;

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-muted-foreground">Verificando acceso...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/auth" replace />;
  }

  // If maintenance is strictly active and user is not admin, redirect to maintenance page
  if (isMaintenanceActive && !isActuallyAdmin && window.location.pathname !== '/mantenimiento') {
    return <Navigate to="/mantenimiento" replace />;
  }

  // If checkSubscription is enabled, user is not admin and not subscribed, redirect to plans
  if (checkSubscription && !isActuallyAdmin && !isSubscribed) {
    return <Navigate to="/planes" replace />;
  }

  return <>{children}</>;
}
